/**
 * Document links: a small "Listen" button after each link to a PDF, which opens
 * the document in the reader view (ReadingVox.readDocument).
 */
import { t } from '../i18n';

const MARK = 'data-rv-doc';
const WIDGET_UI = '.rv-toolbar, .rv-tb-dropdown, .rv-player, .rv-doc-reader, .rv-context-menu, .rv-simplify-overlay';

const SPEAKER = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M19 5a10 10 0 0 1 0 14"/></svg>';

export function isDocumentLink(a: HTMLAnchorElement): boolean {
  if (!a.href) return false;
  if ((a.getAttribute('type') || '').toLowerCase() === 'application/pdf') return true;
  try {
    const u = new URL(a.href, location.href);
    return (u.protocol === 'https:' || u.protocol === 'http:') && /\.pdf$/i.test(u.pathname);
  } catch {
    return false;
  }
}

/** Add buttons under `root`; returns how many were added */
export function addDocumentButtons(root: ParentNode, onListen: (url: string, button: HTMLButtonElement) => void): number {
  let added = 0;
  root.querySelectorAll<HTMLAnchorElement>('a[href]').forEach(a => {
    if (a.hasAttribute(MARK) || a.closest(WIDGET_UI) || a.closest('[contenteditable="true"]') || !isDocumentLink(a)) return;
    a.setAttribute(MARK, '');
    const name = (a.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 80) || t('this document');
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'rv-doc-listen';
    btn.innerHTML = SPEAKER;
    btn.title = t('Listen to this PDF');
    btn.setAttribute('aria-label', t('Listen to {name}', { name }));
    btn.style.cssText = [
      'display:inline-flex', 'align-items:center', 'justify-content:center', 'vertical-align:middle',
      'width:1.6em', 'height:1.6em', 'min-width:24px', 'min-height:24px', 'margin:0 0 0 .35em', 'padding:0',
      'border:1px solid currentColor', 'border-radius:5px', 'background:transparent', 'color:inherit',
      'cursor:pointer', 'opacity:.75', 'font:inherit', 'line-height:1', 'box-sizing:border-box',
    ].join(';');
    btn.addEventListener('mouseenter', () => { btn.style.opacity = '1'; });
    btn.addEventListener('mouseleave', () => { btn.style.opacity = '.75'; });
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      onListen(a.href, btn);
    });
    a.insertAdjacentElement('afterend', btn);
    added++;
  });
  return added;
}

/** Keep adding buttons as the page adds links (debounced). Returns a stop function. */
export function watchDocumentLinks(onListen: (url: string, button: HTMLButtonElement) => void): () => void {
  addDocumentButtons(document, onListen);
  let timer: number | undefined;
  const observer = new MutationObserver((records) => {
    if (!records.some(r => Array.from(r.addedNodes).some(n => n.nodeType === Node.ELEMENT_NODE))) return;
    window.clearTimeout(timer);
    timer = window.setTimeout(() => addDocumentButtons(document, onListen), 800);
  });
  observer.observe(document.body, { childList: true, subtree: true });
  return () => { observer.disconnect(); window.clearTimeout(timer); };
}
